import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { BigRead } from "scmp-app/components/posties/content-homepage/big-read";
import { EyeOnTheNews } from "scmp-app/components/posties/content-homepage/eye-on-the-news";
import { HaHaHappenings } from "scmp-app/components/posties/content-homepage/ha-ha-happenings";
import { HealthAndHappiness } from "scmp-app/components/posties/content-homepage/health-and-happiness";
import { SchoolOfTheWeek } from "scmp-app/components/posties/content-homepage/school-of-the-week";
import { SteamStudio } from "scmp-app/components/posties/content-homepage/steam-studio";
import { ThingsToDo } from "scmp-app/components/posties/content-homepage/things-to-do";
import { TodaysRead } from "scmp-app/components/posties/content-homepage/todays-read";
import { YourSay } from "scmp-app/components/posties/content-homepage/your-say";
import { PostiesSeo } from "scmp-app/components/posties/posties-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { kidsPostiesPageQuery } from "scmp-app/queries/__generated__/kidsPostiesPageQuery.graphql";

import { PageContainer, StyledAppFooter } from "./styles";

const query = graphql`
  query kidsPostiesPageQuery {
    ...bigReadQuery
    ...eyeOnTheNewsQuery
    ...haHaHappeningsQuery
    ...healthAndHappinessQuery
    ...thingsToDoQuery
    ...steamStudioQuery
    ...todaysReadQuery
    ...yourSayQuery
  }
`;

const PostiesKids: RelayComponent<unknown, kidsPostiesPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);

  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  return (
    <>
      <PostiesSeo />
      <PageContainer>
        <TodaysRead reference={data} />
        <BigRead reference={data} />
        <EyeOnTheNews reference={data} />
        <HaHaHappenings reference={data} />
        <ThingsToDo reference={data} />
        <SchoolOfTheWeek />
        <YourSay reference={data} />
        <SteamStudio reference={data} />
        <HealthAndHappiness reference={data} />
        <StyledAppFooter variant="posties" />
      </PageContainer>
    </>
  );
};

PostiesKids.displayName = "PostiesKids";

const pageProps: PageProps = {
  appBarConfiguration: {
    variant: "posties/generic",
  },
  appInitializerConfiguration: {
    application: "posties",
  },
};

export default withRelay(PostiesKids, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
});
